/**
 * Page Notifications — alertes système classées (résultats, examens, paiements, etc.).
 * La messagerie scolarité vit dans /portail/messagerie?tab=scolarite (exclue ici).
 *
 * Refonte : le champ `type_notif` de la BDD mélange sémantique (success/warning/info/danger)
 * et catégoriel (chat/rappel_examen/depot_modif_…). Comme rétro-typer la BDD demanderait
 * de toucher ~30 endpoints, on catégorise ici via des heuristiques sur `titre` + `lien` +
 * `type_notif`, puis on regroupe par catégorie ET par date pour sortir du pêle-mêle.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Send, CheckCheck, Loader2, Inbox, Check,
  BellRing, AlertCircle,
  Trash2, X, ChevronDown, ChevronRight,
  Award, GraduationCap, CreditCard, MessageSquare, Megaphone, Clock, FileText,
} from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi, NotificationItem } from '../services/portailApi';
import { usePushNotifications } from '../hooks/usePushNotifications';

// ── Catégorisation ────────────────────────────────────────────────────────
type CategoryKey =
  | 'resultats' | 'examens' | 'paiements' | 'reclamations'
  | 'messages'  | 'annonces' | 'rappels'   | 'autres';

interface CategoryInfo {
  key: CategoryKey;
  label: string;
  Icon: React.ComponentType<any>;
  color: string;      // tailwind text color
  bgSoft: string;     // tailwind bg tint for icon halo
}

const CATEGORIES: Record<CategoryKey, CategoryInfo> = {
  resultats:    { key: 'resultats',    label: 'Résultats & notes',   Icon: Award,         color: 'text-emerald-400', bgSoft: 'bg-emerald-500/10' },
  examens:      { key: 'examens',      label: 'Examens & salles',    Icon: GraduationCap, color: 'text-sky-400',     bgSoft: 'bg-sky-500/10'     },
  paiements:    { key: 'paiements',    label: 'Paiements',           Icon: CreditCard,    color: 'text-amber-400',   bgSoft: 'bg-amber-500/10'   },
  reclamations: { key: 'reclamations', label: 'Réclamations',        Icon: AlertCircle,   color: 'text-rose-400',    bgSoft: 'bg-rose-500/10'    },
  messages:     { key: 'messages',     label: 'Messages',            Icon: MessageSquare, color: 'text-indigo-400',  bgSoft: 'bg-indigo-500/10'  },
  annonces:     { key: 'annonces',     label: 'Annonces',            Icon: Megaphone,     color: 'text-purple-400',  bgSoft: 'bg-purple-500/10'  },
  rappels:      { key: 'rappels',      label: 'Rappels',             Icon: Clock,         color: 'text-cyan-400',    bgSoft: 'bg-cyan-500/10'    },
  autres:       { key: 'autres',       label: 'Autres',              Icon: FileText,      color: 'text-gray-400',    bgSoft: 'bg-gray-500/10'    },
};

const ORDRE_CATS: CategoryKey[] = [
  'resultats', 'reclamations', 'examens', 'paiements', 'messages', 'annonces', 'rappels', 'autres',
];

function classer(n: NotificationItem): CategoryKey {
  const t = (n.type_notif || '').toLowerCase();
  const lien = (n.lien || '').toLowerCase();
  const titre = (n.titre || '').toLowerCase();
  const msg = (n.message || '').toLowerCase();
  const hay = titre + ' ' + msg;

  // Signaux forts via lien (priorité)
  if (lien.includes('/releve') || lien.includes('/historique')) return 'resultats';
  if (lien.includes('/mes-reclamations') || lien.includes('/reclamation')) return 'reclamations';
  if (lien.includes('/emplois-temps') || lien.includes('/ma-salle')) return 'examens';
  if (lien.includes('/messagerie') || lien.includes('/messages')) return 'messages';
  if (lien.includes('/actualites') || lien.includes('/anniversaires')) return 'annonces';

  // Signaux via type_notif catégoriel
  if (t === 'rappel_examen' || t === 'rappel-depot') return 'examens';
  if (t === 'rappel') return 'rappels';
  if (t === 'chat' || t === 'message-interne' || t === 'message_envoye') return 'messages';
  if (t.startsWith('depot_')) return 'examens';

  // Heuristiques texte
  if (/(résultat|resultat|moyenne|note|releve|relevé|délibération|deliberation|rang|admis|ajourn)/i.test(hay)) return 'resultats';
  if (/(réclamation|reclamation|contestation)/i.test(hay)) return 'reclamations';
  if (/(examen|composition|salle|surveillance|convocation|absence)/i.test(hay)) return 'examens';
  if (/(paiement|scolarit[ée]|solde|frais|reçu|recu|caution|inscription)/i.test(hay)) return 'paiements';
  if (/(anniversaire|fêt|fete|événement|evenement|annonce|actualit)/i.test(hay)) return 'annonces';
  if (/(rappel|échéance|echeance|deadline)/i.test(hay)) return 'rappels';
  if (/(message|discussion|conversation)/i.test(hay)) return 'messages';

  return 'autres';
}

// ── Utilitaires date ─────────────────────────────────────────────────────
function fmtDateFull(s: string): string {
  return new Date(s).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtRelatif(s: string): string {
  const t = new Date(s).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.round((now - t) / 1000));
  if (diffSec < 60) return "à l'instant";
  const min = Math.round(diffSec / 60);
  if (min < 60) return `il y a ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.round(h / 24);
  if (j === 1) return 'hier';
  if (j < 7) return `il y a ${j} j`;
  return new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

type BucketKey = 'today' | 'yesterday' | 'week' | 'older';
function bucketDate(s: string): BucketKey {
  const now = new Date();
  const d = new Date(s);
  const midNow = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const midD = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((midNow - midD) / 86_400_000);
  if (dayDiff <= 0) return 'today';
  if (dayDiff === 1) return 'yesterday';
  if (dayDiff < 7) return 'week';
  return 'older';
}
const BUCKET_LABEL: Record<BucketKey, string> = {
  today: "Aujourd'hui",
  yesterday: 'Hier',
  week: 'Cette semaine',
  older: 'Plus ancien',
};
const BUCKET_ORDER: BucketKey[] = ['today', 'yesterday', 'week', 'older'];

// ── Composant principal ──────────────────────────────────────────────────
type Filtre = CategoryKey | 'toutes' | 'non_lues';

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [marking, setMarking] = useState(false);
  const [filtre, setFiltre] = useState<Filtre>('toutes');
  const [collapsed, setCollapsed] = useState<Set<CategoryKey>>(new Set());

  // Mode sélection multiple (WhatsApp-style : long-press pour activer, tap pour cocher)
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      if (next.size === 0) setSelectionMode(false);
      return next;
    });
  };

  const startLongPress = (id: number) => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      setSelectionMode(true);
      setSelectedIds(new Set([id]));
      try { (navigator as any).vibrate?.(40); } catch { /* */ }
    }, 450);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Supprimer ${selectedIds.size} notification(s) ?`)) return;
    try {
      const ids = Array.from(selectedIds);
      await portailApi.supprimerNotifications(ids);
      setItems(prev => prev.filter(n => !selectedIds.has(n.id)));
      exitSelectionMode();
    } catch (e: any) {
      alert(e?.message || 'Erreur suppression');
    }
  };

  const push = usePushNotifications();
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<
    { type: 'success' | 'error' | 'warning'; text: string } | null
  >(null);

  const handleTestPush = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await portailApi.testPush();
      if (r.nb_subscriptions_actives === 0) {
        setTestResult({
          type: 'warning',
          text: "Aucun abonnement actif sur cet appareil. Activez d'abord les notifications.",
        });
      } else if (r.nb_pushs_envoyees === 0) {
        setTestResult({
          type: 'error',
          text: `${r.nb_subscriptions_actives} abonnement(s) trouvé(s) mais aucun message n'a pu être délivré. Réactivez les notifications.`,
        });
      } else {
        setTestResult({
          type: 'success',
          text: `Test envoyé sur ${r.nb_pushs_envoyees}/${r.nb_subscriptions_actives} appareil(s). Vous devriez recevoir une notification dans quelques secondes.`,
        });
      }
    } catch (e: any) {
      setTestResult({ type: 'error', text: e?.message || 'Erreur lors du test' });
    } finally {
      setTesting(false);
    }
  };

  const charger = () => {
    setLoading(true);
    setError(null);
    portailApi.notifications()
      .then((r) => setItems(r.results))
      .catch((e: any) => {
        if (e.status === 401) {
          portailApi.clearToken();
          navigate('/portail/login', { replace: true });
          return;
        }
        setError(e?.message || "Impossible de charger les notifications.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { charger(); }, []);

  const handleClick = async (n: NotificationItem) => {
    // Fix 2026-09-07 : mise à jour optimiste pour que le badge chute
    // immédiatement, indépendamment du fetch réseau.
    if (!n.lu) {
      setItems((prev) => prev.map((it) => it.id === n.id ? { ...it, lu: true } : it));
      portailApi.marquerNotifLue(n.id).catch(() => { /* ignore */ });
    }
    // Fix 2026-09-07 : ouvre la ressource cible. Si aucun lien fiable, on
    // reste sur la page Notifications au lieu de tomber "en accueil".
    if (n.lien) {
      if (n.lien.startsWith('/portail/')) navigate(n.lien);
      else if (n.lien.startsWith('/')) navigate(n.lien);
      // sinon (lien externe) — ignore, l'utilisateur voit la notif marquée
    }
  };

  const handleMarkAll = async () => {
    setMarking(true);
    try {
      await portailApi.marquerToutesLues();
      setItems((prev) => prev.map((it) => ({ ...it, lu: true })));
    } catch (e: any) {
      alert(e?.message || 'Erreur');
    } finally {
      setMarking(false);
    }
  };

  // Filtrer : on n'affiche pas les notifs "message_envoye" (elles vivent dans /messagerie)
  const listeBase = useMemo(
    () => items.filter((n) => n.type_notif !== 'message_envoye'),
    [items],
  );

  // Enrichir avec la catégorie calculée + trier par date desc une seule fois
  const enrichi = useMemo(() => {
    return listeBase
      .map((n) => ({ n, cat: classer(n) }))
      .sort((a, b) => new Date(b.n.date_creation).getTime() - new Date(a.n.date_creation).getTime());
  }, [listeBase]);

  // Compteurs par catégorie (toutes) + non lues
  const countsCat = useMemo(() => {
    const c: Record<CategoryKey, number> = {
      resultats: 0, examens: 0, paiements: 0, reclamations: 0,
      messages: 0, annonces: 0, rappels: 0, autres: 0,
    };
    enrichi.forEach(({ cat }) => { c[cat]++; });
    return c;
  }, [enrichi]);

  const nbNonLuesTotal = useMemo(() => enrichi.filter(({ n }) => !n.lu).length, [enrichi]);

  // Application du filtre actif
  const listeFiltree = useMemo(() => {
    if (filtre === 'toutes') return enrichi;
    if (filtre === 'non_lues') return enrichi.filter(({ n }) => !n.lu);
    return enrichi.filter(({ cat }) => cat === filtre);
  }, [enrichi, filtre]);

  // Groupement : catégorie → bucket date → items
  const groupes = useMemo(() => {
    const g: Record<CategoryKey, Record<BucketKey, NotificationItem[]>> = {} as any;
    ORDRE_CATS.forEach((k) => {
      g[k] = { today: [], yesterday: [], week: [], older: [] };
    });
    listeFiltree.forEach(({ n, cat }) => {
      g[cat][bucketDate(n.date_creation)].push(n);
    });
    return g;
  }, [listeFiltree]);

  const toggleCollapse = (k: CategoryKey) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const catsActives = ORDRE_CATS.filter((k) =>
    Object.values(groupes[k]).some((arr) => arr.length > 0),
  );

  const renderItem = (n: NotificationItem, cat: CategoryKey) => {
    const info = CATEGORIES[cat];
    const Icon = info.Icon;
    const nonLu = !n.lu;
    const selected = selectedIds.has(n.id);
    return (
      <div
        key={n.id}
        onClick={() => {
          if (selectionMode) toggleSelect(n.id);
          else handleClick(n);
        }}
        onTouchStart={() => startLongPress(n.id)}
        onTouchEnd={cancelLongPress}
        onTouchCancel={cancelLongPress}
        onMouseDown={() => startLongPress(n.id)}
        onMouseUp={cancelLongPress}
        onMouseLeave={cancelLongPress}
        className={`px-3 py-2.5 transition-colors select-none border-l-2 ${
          selected
            ? 'bg-blue-900/25 border-blue-500'
            : nonLu
              ? 'bg-gray-800/40 border-blue-500/60 hover:bg-gray-800/70'
              : 'bg-transparent border-transparent hover:bg-gray-800/30'
        } ${(n.lien || nonLu || selectionMode) ? 'cursor-pointer' : ''}`}
      >
        <div className="flex items-start gap-2.5">
          {selectionMode && (
            <div className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
              selected ? 'border-blue-500 bg-blue-500' : 'border-gray-500'
            }`}>
              {selected && <Check size={10} className="text-white" />}
            </div>
          )}
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${info.bgSoft}`}>
            <Icon size={15} className={info.color} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <div className={`text-[13px] flex-1 leading-tight ${nonLu ? 'font-semibold text-gray-100' : 'text-gray-300'}`}>
                {n.titre}
              </div>
              {nonLu && !selectionMode && (
                <span className="w-1.5 h-1.5 bg-blue-400 rounded-full flex-shrink-0" title="Non lue" />
              )}
            </div>
            <div className="text-[12px] text-gray-400 whitespace-pre-wrap break-words leading-snug mt-0.5">
              {n.message}
            </div>
            <div className="flex items-center justify-between mt-1 text-[10px] text-gray-500">
              <span title={fmtDateFull(n.date_creation)}>
                {fmtRelatif(n.date_creation)}
              </span>
              {n.lu && (
                <span className="inline-flex items-center gap-0.5 text-gray-600">
                  <Check size={10} /> Lu
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Rendu ──────────────────────────────────────────────────────────────
  return (
    <PortailLayout showHeader title="Notifications">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/portail/accueil')}
            className="p-2 hover:bg-gray-800 rounded text-gray-300"
            title="Retour"
          >
            <ArrowLeft size={18} />
          </button>
          <h2 className="font-semibold text-gray-100 flex-1 text-sm">
            Notifications
            {nbNonLuesTotal > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold bg-red-500 text-white rounded-full px-1">
                {nbNonLuesTotal}
              </span>
            )}
          </h2>
        </div>

        {/* Diagnostic push */}
        {push.supported && (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <BellRing size={16} className="text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-xs text-gray-100">
                  Tester mes notifications
                </div>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  Envoie une notification de test à cet appareil pour vérifier votre configuration.
                </p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {!push.enabled && push.permission !== 'denied' && (
                    <button
                      onClick={() => push.enable()}
                      disabled={push.loading}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-[11px] font-medium rounded"
                    >
                      {push.loading ? 'Activation…' : 'Activer'}
                    </button>
                  )}
                  <button
                    onClick={handleTestPush}
                    disabled={testing || !push.enabled}
                    className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-gray-100 text-[11px] font-medium rounded inline-flex items-center gap-1.5"
                    title={!push.enabled ? "Activez d'abord les notifications" : ''}
                  >
                    {testing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                    {testing ? 'Envoi…' : 'Envoyer un test'}
                  </button>
                  {push.permission === 'denied' && (
                    <span className="text-[10px] text-red-400 inline-flex items-center gap-1">
                      <AlertCircle size={11} />
                      Permission refusée — autorisez dans le navigateur
                    </span>
                  )}
                </div>
                {testResult && (
                  <div
                    className={`mt-2 p-2 rounded text-[11px] border ${
                      testResult.type === 'success'
                        ? 'bg-green-900/30 text-green-300 border-green-800'
                        : testResult.type === 'warning'
                          ? 'bg-orange-900/30 text-orange-300 border-orange-800'
                          : 'bg-red-900/30 text-red-300 border-red-800'
                    }`}
                  >
                    {testResult.text}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Barre chips filtres */}
        {enrichi.length > 0 && (
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
            {([
              { key: 'toutes',   label: 'Toutes',   count: enrichi.length },
              { key: 'non_lues', label: 'Non lues', count: nbNonLuesTotal, highlight: true },
              ...ORDRE_CATS
                .filter((k) => countsCat[k] > 0)
                .map((k) => ({ key: k, label: CATEGORIES[k].label, count: countsCat[k] })),
            ] as Array<{ key: Filtre; label: string; count: number; highlight?: boolean }>).map((chip) => {
              const actif = filtre === chip.key;
              return (
                <button
                  key={chip.key}
                  onClick={() => setFiltre(chip.key)}
                  className={`whitespace-nowrap px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors flex items-center gap-1.5 ${
                    actif
                      ? 'bg-blue-600 border-blue-500 text-white'
                      : chip.highlight && chip.count > 0
                        ? 'bg-red-900/30 border-red-800 text-red-300 hover:bg-red-900/50'
                        : 'bg-gray-900 border-gray-800 text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  {chip.label}
                  <span className={`inline-flex items-center justify-center min-w-[16px] h-[14px] rounded text-[9px] font-bold px-1 ${
                    actif ? 'bg-white/20' : 'bg-gray-800 text-gray-400'
                  }`}>
                    {chip.count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {nbNonLuesTotal > 0 && (
          <button
            onClick={handleMarkAll}
            disabled={marking}
            className="w-full py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 text-xs rounded inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {marking ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />}
            Marquer toutes comme lues
          </button>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-400">
            <Loader2 className="inline-block animate-spin w-8 h-8 text-blue-500" />
            <p className="text-sm mt-3">Chargement...</p>
          </div>
        ) : error ? (
          <div className="bg-red-900/20 border border-red-800 rounded-lg p-6 text-center">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-400" />
            <p className="text-red-300 text-sm">{error}</p>
            <button onClick={charger}
              className="mt-3 px-3 py-1.5 text-xs bg-red-700 hover:bg-red-600 text-white rounded">
              Réessayer
            </button>
          </div>
        ) : enrichi.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 text-center">
            <Inbox className="w-12 h-12 mx-auto text-gray-500 mb-3" />
            <p className="text-gray-300 font-medium">Aucune notification</p>
            <p className="text-xs text-gray-500 mt-1">
              Pour écrire à la scolarité ou à vos camarades, ouvrez la{' '}
              <button
                onClick={() => navigate('/portail/messagerie')}
                className="text-blue-400 hover:underline"
              >
                messagerie
              </button>.
            </p>
          </div>
        ) : catsActives.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 text-center">
            <Inbox className="w-10 h-10 mx-auto text-gray-500 mb-2" />
            <p className="text-gray-300 text-sm font-medium">Rien à afficher</p>
            <p className="text-xs text-gray-500 mt-1">Aucune notification dans ce filtre.</p>
          </div>
        ) : (
          <>
            {/* Barre d'action en mode sélection (WhatsApp-style) */}
            {selectionMode && (
              <div className="sticky top-0 z-10 bg-gray-900 border border-blue-700 rounded-lg p-2 mb-2 flex items-center gap-2 shadow-lg">
                <button onClick={exitSelectionMode}
                  className="p-1.5 hover:bg-gray-800 rounded text-gray-300">
                  <X size={16} />
                </button>
                <span className="text-sm font-semibold text-gray-100 flex-1">
                  {selectedIds.size} sélectionnée{selectedIds.size > 1 ? 's' : ''}
                </span>
                <button
                  onClick={() => {
                    const allIds = new Set(listeFiltree.map(({ n }) => n.id));
                    if (selectedIds.size === listeFiltree.length) {
                      setSelectedIds(new Set());
                      setSelectionMode(false);
                    } else {
                      setSelectedIds(allIds);
                    }
                  }}
                  className="text-xs text-blue-400 hover:text-blue-300 px-2"
                >
                  {selectedIds.size === listeFiltree.length ? 'Aucun' : 'Tout'}
                </button>
                <button onClick={handleDeleteSelected}
                  className="p-1.5 bg-red-600 hover:bg-red-700 rounded text-white">
                  <Trash2 size={16} />
                </button>
              </div>
            )}

            <div className="space-y-3">
              {catsActives.map((catKey) => {
                const info = CATEGORIES[catKey];
                const CatIcon = info.Icon;
                const seaux = groupes[catKey];
                const totalCat = BUCKET_ORDER.reduce((s, b) => s + seaux[b].length, 0);
                const nonLuesCat = BUCKET_ORDER.reduce(
                  (s, b) => s + seaux[b].filter((n) => !n.lu).length, 0,
                );
                const isCollapsed = collapsed.has(catKey);
                return (
                  <section
                    key={catKey}
                    className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden"
                  >
                    {/* Header catégorie */}
                    <button
                      onClick={() => toggleCollapse(catKey)}
                      className="w-full flex items-center gap-2 px-3 py-2 border-b border-gray-800/70 hover:bg-gray-800/40 transition-colors"
                    >
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${info.bgSoft}`}>
                        <CatIcon size={14} className={info.color} />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="text-[13px] font-semibold text-gray-100">
                          {info.label}
                        </div>
                        <div className="text-[10px] text-gray-500">
                          {totalCat} notification{totalCat > 1 ? 's' : ''}
                          {nonLuesCat > 0 && ` · ${nonLuesCat} non lue${nonLuesCat > 1 ? 's' : ''}`}
                        </div>
                      </div>
                      {nonLuesCat > 0 && (
                        <span className="min-w-[18px] h-[18px] inline-flex items-center justify-center text-[10px] font-bold bg-red-500 text-white rounded-full px-1">
                          {nonLuesCat}
                        </span>
                      )}
                      {isCollapsed
                        ? <ChevronRight size={14} className="text-gray-500" />
                        : <ChevronDown size={14} className="text-gray-500" />}
                    </button>

                    {/* Contenu catégorie */}
                    {!isCollapsed && (
                      <div className="divide-y divide-gray-800/70">
                        {BUCKET_ORDER.filter((b) => seaux[b].length > 0).map((b) => (
                          <div key={b}>
                            <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-gray-500 font-semibold bg-gray-900/60">
                              {BUCKET_LABEL[b]}
                            </div>
                            <div className="divide-y divide-gray-800/50">
                              {seaux[b].map((n) => renderItem(n, catKey))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>

            {!selectionMode && listeFiltree.length > 0 && (
              <div className="text-[10px] text-gray-500 text-center mt-2">
                Appui long sur une notification pour activer la sélection multiple
              </div>
            )}
          </>
        )}
      </div>
    </PortailLayout>
  );
}
